/**
 * TeamDashboardPage
 * 
 * Página de Cockpit de Performance Individual.
 * Exibe métricas de vendas, metas, pipeline e estatísticas do usuário logado.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Award, Clock, Percent,
  Briefcase, Activity, Rocket, TrendingUp, ScrollText, Crown, Leaf
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import Avatar from '../../../components/Avatar/Avatar';
import { cockpitService } from '../../../services/cockpitService';
import CockpitPrizeRulesModal from './CockpitPrizeRulesModal';
import './TeamDashboardPage.css';

const TeamDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [prizeRulesOpen, setPrizeRulesOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cockpitService.getPerformance();
      if (response.success) {
        setDashboardData(response.data);
      } else {
        setError(response.message || 'Erro ao carregar dados');
      }
    } catch (err) {
      console.error('Erro ao carregar dados do cockpit:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const userType = dashboardData?.userType || 'vendedor';
  const isG1 = userType === 'G.1';
  const personalSales = dashboardData?.personalSales ?? dashboardData?.currentSales ?? 0;
  const teamSales = dashboardData?.teamSales ?? (isG1 ? dashboardData?.currentSales ?? 0 : personalSales);
  const milestones = useMemo(() => dashboardData?.milestones ?? [], [dashboardData?.milestones]);
  const monthlyGoal = dashboardData?.monthlyGoal ?? 0;
  const teamMilestone = dashboardData?.teamMilestone ?? dashboardData?.currentMilestone ?? null;
  const personalFunnel = dashboardData?.personalFunnel || [
    { label: 'Negociações Ativas', count: 0, color: 'blue' },
    { label: 'Propostas Enviadas', count: 0, color: 'primary' },
    { label: 'Aguardando Assinatura', count: 0, color: 'emerald' },
  ];
  const conversionRate = dashboardData?.conversionRate || 0;
  const averageTicket = dashboardData?.averageTicket || 0;
  const averageCycle = dashboardData?.averageCycle || 0;
  const slaResponse = dashboardData?.slaResponse || 0;
  const nextStep = dashboardData?.nextStep || null;
  const prizeEligible = dashboardData?.prizeEligible !== false;
  const totalG1Prize = dashboardData?.totalG1Prize || 0;
  const currentPrize = dashboardData?.currentPrize ?? 0;
  const teamBasePrize = dashboardData?.teamBasePrize ?? 0;
  const topSellerBonusAmount = dashboardData?.topSellerBonus ?? 0;
  const sellersPerformance = dashboardData?.sellersPerformance || [];
  const isTopSeller = dashboardData?.isTopSeller === true;
  const leadershipChallenge = dashboardData?.leadershipChallenge;
  const leadershipStreak = dashboardData?.leadershipStreak;
  const topSellerLeader = sellersPerformance.find((seller) => seller.isTopSeller);
  const showLeadershipLeaves = leadershipStreak?.currentMonthIsLeader && leadershipStreak.leaves > 0;

  const displayPrize = useMemo(() => {
    if (isG1) {
      return totalG1Prize;
    }
    return currentPrize;
  }, [isG1, totalG1Prize, currentPrize]);

  const progressPercent = monthlyGoal > 0
    ? Math.min((teamSales / monthlyGoal) * 100, 100)
    : 0;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="team-dashboard">
        <div className="team-dashboard-loading">
          <p>Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="team-dashboard">
        <div className="team-dashboard-error">
          <p>{error}</p>
          <button onClick={loadDashboardData}>Tentar novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div className="team-dashboard">
      {/* Header Individual */}
      <div className="team-dashboard-header">
        <div className="team-dashboard-header-left">
          <div className="team-dashboard-avatar-wrapper">
            <Avatar name={user?.name || 'User'} imageUrl={user?.avatar_url} size="xl" />
            <div className="team-dashboard-avatar-badge">
              <Activity size={14} />
            </div>
          </div>
          <div>
            <h1 className="team-dashboard-title">
              {isG1 ? 'Performance da ' : 'Minha '}<span className="team-dashboard-title-highlight">{isG1 ? 'Equipe' : 'Performance'}</span>
            </h1>
            <p className="team-dashboard-subtitle">
              {isG1 ? 'Cockpit Gerencial' : 'Cockpit de Resultados'}
            </p>
          </div>
        </div>
        
        <div className="team-dashboard-header-actions">
          {prizeEligible && (leadershipChallenge || (isG1 && topSellerLeader)) && (
            <div
              className={`team-dashboard-leadership-chip${leadershipChallenge?.isTopSeller ? ' is-leader' : ''}`}
              tabIndex={0}
              aria-label={leadershipChallenge?.message || `Liderança: ${topSellerLeader?.name || 'equipe'}`}
            >
              <Crown size={22} aria-hidden />
              <div className="team-dashboard-leadership-tooltip" role="tooltip">
                <p>
                  {leadershipChallenge?.message || (
                    <>
                      <strong>{topSellerLeader?.name}</strong> lidera com {formatCurrency(topSellerLeader?.sales || 0)}
                      {(topSellerLeader?.topSellerPrize ?? topSellerLeader?.topSellerBonus) > 0 && (
                        <> (+ {formatCurrency(topSellerLeader.topSellerPrize ?? topSellerLeader.topSellerBonus)} líder)</>
                      )}
                    </>
                  )}
                </p>
                {showLeadershipLeaves && leadershipStreak.reward && (
                  <p className="team-dashboard-leadership-streak-note">
                    {leadershipStreak.reward.label}
                  </p>
                )}
                {leadershipChallenge && !leadershipChallenge.isTopSeller && (
                  <button
                    type="button"
                    className="team-dashboard-leadership-tooltip-action"
                    onClick={() => navigate('/erp/prospecting')}
                  >
                    Ir para vendas
                  </button>
                )}
              </div>
            </div>
          )}

          {showLeadershipLeaves && (
            <div
              className="team-dashboard-streak-leaves"
              aria-label={`${leadershipStreak.leaves} mês(es) consecutivo(s) como líder`}
            >
              {Array.from({ length: leadershipStreak.leaves }).map((_, index) => (
                <div
                  key={index}
                  className={`team-dashboard-streak-leaf-badge team-dashboard-streak-leaf-badge--${index + 1}`}
                >
                  <Leaf size={24} strokeWidth={2.25} aria-hidden />
                </div>
              ))}
            </div>
          )}

          {prizeEligible && (
          <div className="team-dashboard-bonus-card">
            <button
              type="button"
              className="team-dashboard-bonus-rules-btn"
              onClick={() => setPrizeRulesOpen(true)}
              title="Regras de premiação"
              aria-label="Abrir regras de premiação"
            >
              <ScrollText size={18} />
            </button>
            <div className="team-dashboard-bonus-icon">
              <Trophy size={24} />
            </div>
            <div>
              <p className="team-dashboard-bonus-label">
                {isG1 ? 'Prêmio Total da Equipe' : 'Meu Prêmio Estimado'}
              </p>
              <p className="team-dashboard-bonus-value">{formatCurrency(displayPrize)}</p>
              {!isG1 && prizeEligible && (teamBasePrize > 0 || topSellerBonusAmount > 0) && (
                <p className="team-dashboard-bonus-note">
                  {teamBasePrize > 0 && <>Coletivo: {formatCurrency(teamBasePrize)}</>}
                  {isTopSeller && topSellerBonusAmount > 0 && (
                    <>{teamBasePrize > 0 ? ' · ' : ''}+{formatCurrency(topSellerBonusAmount)} líder</>
                  )}
                </p>
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Sales Goal Tracker Individual */}
      <div className="team-dashboard-goal-card">
        <div className="team-dashboard-goal-background" />
        
        <div className="team-dashboard-goal-content">
          <div className="team-dashboard-goal-header">
            <div className="team-dashboard-goal-header-left">
              <div className="team-dashboard-goal-icon">
                <Rocket size={28} />
              </div>
              <div>
                <h3 className="team-dashboard-goal-title">
                  {isG1 || prizeEligible ? 'Meta Coletiva da Empresa' : 'Minha Meta Mensal'}
                </h3>
                <p className="team-dashboard-goal-subtitle">
                  {isG1 || prizeEligible
                    ? `Minhas vendas: ${formatCurrency(personalSales)}`
                    : `Objetivo: ${formatCurrency(monthlyGoal)}`}
                </p>
              </div>
            </div>
            <div className="team-dashboard-goal-header-right">
              <span className="team-dashboard-goal-amount">{formatCurrency(teamSales)}</span>
              <div className="team-dashboard-goal-status">
                <div className="team-dashboard-goal-status-dot" />
                <p className="team-dashboard-goal-status-text">
                  Apenas {formatCurrency(Math.max(monthlyGoal - teamSales, 0))} para o topo
                </p>
              </div>
            </div>
          </div>

          {/* Individual Progress Bar */}
          <div className="team-dashboard-progress-container">
            <div className="team-dashboard-progress-bar">
              <div 
                className="team-dashboard-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
              
              {/* Milestones Markers */}
              {milestones.map((m, idx) => {
                const pos = monthlyGoal > 0
                  ? Math.min(Math.max((m.value / monthlyGoal) * 100, 4), 96)
                  : 0;
                let isReached;
                if (isG1 || prizeEligible) {
                  const g1Bonus = m.g1Bonus || { allReached: false };
                  isReached = g1Bonus.allReached || teamSales >= m.value;
                } else {
                  isReached = personalSales >= m.value;
                }
                const isCurrentTier = (isG1 || prizeEligible) && teamMilestone?.value === m.value;
                const labelPlacement = idx % 2 === 0 ? 'below' : 'above';
                return (
                  <div
                    key={idx}
                    className={`team-dashboard-milestone team-dashboard-milestone--${labelPlacement}${isCurrentTier ? ' is-current-tier' : ''}`}
                    style={{ left: `${pos}%` }}
                  >
                    <div className={`team-dashboard-milestone-line ${isReached ? 'reached' : ''}`} />
                    <div className="team-dashboard-milestone-content">
                      <div className={`team-dashboard-milestone-badge ${isReached ? 'reached' : ''}`}>
                        <Award size={18} />
                      </div>
                      <div className="team-dashboard-milestone-info">
                        <span className={`team-dashboard-milestone-value ${isReached ? 'reached' : ''}`}>
                          {formatCurrency(m.value)}
                        </span>
                        <span className={`team-dashboard-milestone-prize ${isReached ? 'reached' : ''}`}>
                          {isG1 ? (
                            <>
                              <span className="team-dashboard-milestone-prize-line">
                                Geral {formatCurrency(m.prize)}
                              </span>
                              <span className="team-dashboard-milestone-prize-line">
                                {(m.g1Bonus?.sellersCount ?? 0)}/{sellersPerformance.length || 0} no patamar
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="team-dashboard-milestone-prize-line">
                                Geral {formatCurrency(m.prize)}
                              </span>
                              <span className="team-dashboard-milestone-prize-line team-dashboard-milestone-prize-line-leader">
                                Líder {(m.top_seller_prize ?? 0) > 0 ? formatCurrency(m.top_seller_prize) : '—'}
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="team-dashboard-grid">
        {/* Personal Pipeline */}
        <div className="team-dashboard-pipeline-card">
          <div className="team-dashboard-pipeline-header">
            <h3 className="team-dashboard-pipeline-title">
              <Briefcase size={16} className="team-dashboard-pipeline-icon" /> Meu Pipeline de Vendas
            </h3>
            <button 
              onClick={() => navigate('/erp/prospecting')} 
              className="team-dashboard-pipeline-link"
            >
              Ver CRM Completo
            </button>
          </div>
          
          <div className="team-dashboard-funnel-grid">
            {personalFunnel.map((item) => (
              <div key={item.label} className="team-dashboard-funnel-item">
                <p className="team-dashboard-funnel-count">{item.count}</p>
                <p className="team-dashboard-funnel-label">{item.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Column */}
        <div className="team-dashboard-stats-column">
          <div className="team-dashboard-conversion-card">
            <div className="team-dashboard-conversion-content">
              <h3 className="team-dashboard-conversion-title">Eficiência de Conversão</h3>
              <div className="team-dashboard-conversion-main">
                <div className="team-dashboard-conversion-left">
                  <div className="team-dashboard-conversion-header">
                    <span className="team-dashboard-conversion-rate">{conversionRate.toFixed(1)}%</span>
                    <div className="team-dashboard-conversion-trend">
                      <TrendingUp size={14} />
                    </div>
                  </div>
                  <p className="team-dashboard-conversion-ticket">Ticket Médio: {formatCurrency(averageTicket)}</p>
                </div>
                <div className="team-dashboard-conversion-chart">
                  {[40, 60, 45, 90, 70, 85].map((h, i) => (
                    <div key={i} className="team-dashboard-conversion-bar">
                      <div 
                        className="team-dashboard-conversion-bar-fill" 
                        style={{ height: `${h}%` }} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="team-dashboard-conversion-gradient" />
          </div>

          <div className="team-dashboard-metrics-grid">
            <div className="team-dashboard-metric-card">
              <Clock size={24} className="team-dashboard-metric-icon clock" />
              <p className="team-dashboard-metric-label">Ciclo Médio</p>
              <p className="team-dashboard-metric-value">{averageCycle.toFixed(1)} Dias</p>
            </div>
            <div className="team-dashboard-metric-card">
              <Percent size={24} className="team-dashboard-metric-icon percent" />
              <p className="team-dashboard-metric-label">SLA Resposta</p>
              <p className="team-dashboard-metric-value">{slaResponse} min</p>
            </div>
          </div>

          {nextStep && (
            <div className="team-dashboard-next-step-card">
              <h4 className="team-dashboard-next-step-title">Próximo Passo</h4>
              <p className="team-dashboard-next-step-text">
                {nextStep.message}
              </p>
              {nextStep.actionUrl && (
                <button 
                  onClick={() => navigate(nextStep.actionUrl)}
                  className="team-dashboard-next-step-button"
                >
                  {nextStep.actionLabel || 'Gerar Proposta Agora'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <CockpitPrizeRulesModal
        isOpen={prizeRulesOpen && prizeEligible}
        onClose={() => setPrizeRulesOpen(false)}
        dashboardData={dashboardData}
      />
    </div>
  );
};

export default TeamDashboardPage;
